import { DefinedUseQueryResult, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { monolithApi } from '@/utils/api';

type WrappedResponse<T> = { data: T };

export interface UseMonolithApiQueryOptions<ResponseType, ErrorType> extends Omit<
  UseQueryOptions<WrappedResponse<ResponseType>, AxiosError<ErrorType>, ResponseType>,
  'queryKey' | 'queryFn'
> {
  requestConfig?: Omit<AxiosRequestConfig, 'url'>;
}

function useMonolithApiQuery<ResponseType, ErrorType>(
  queryKey: QueryKey,
  url: string,
): UseQueryResult<ResponseType, AxiosError<ErrorType>>;
function useMonolithApiQuery<ResponseType, ErrorType>(
  queryKey: QueryKey,
  url: string,
  options: UseMonolithApiQueryOptions<ResponseType, ErrorType> & { initialData: WrappedResponse<ResponseType> },
): DefinedUseQueryResult<ResponseType, AxiosError<ErrorType>>;
function useMonolithApiQuery<ResponseType, ErrorType>(
  queryKey: QueryKey,
  url: string,
  options?: UseMonolithApiQueryOptions<ResponseType, ErrorType>,
): UseQueryResult<ResponseType, AxiosError<ErrorType>>;
function useMonolithApiQuery<ResponseType, ErrorType>(
  queryKey: QueryKey,
  url: string,
  options?: UseMonolithApiQueryOptions<ResponseType, ErrorType>,
): UseQueryResult<ResponseType, AxiosError<ErrorType>> {
  const requestConfig = {
    ...options?.requestConfig,
    paramsSerializer: {
      serialize: (params: string | Record<string | number, string | number | string[] | number[]>): string =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
      ...options?.requestConfig?.paramsSerializer,
    },
  };

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => monolithApi.get(url, { signal, ...requestConfig }),
    select: (response) => response.data,
    ...options,
  });
}

export default useMonolithApiQuery;
